import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import DailyChallenge from "@/components/DailyChallenge";
import { Flame, Dumbbell } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, Check, X } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { earnPoints } from "@/lib/usePoints";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const FLAG_IN = { initial: { opacity: 0, scale: 0.9 }, animate: { opacity: 1, scale: 1 } };
const FEEDBACK_IN = { initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 } };

export default function Quiz() {
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const [params, setParams] = useSearchParams();
  const daily = params.get("daily") === "1";
  const setDaily = (on) => { const p = new URLSearchParams(params); if (on) p.set("daily", "1"); else p.delete("daily"); setParams(p, { replace: true }); };
  const nonDisputed = useMemo(() => countries.filter((c) => !c.disputed), [countries]);

  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(null); // { picked, correct }
  const [question, setQuestion] = useState(null);

  const nextRound = React.useCallback(() => {
    if (nonDisputed.length < 4) return;
    const pool = shuffle(nonDisputed);
    const target = pool[0];
    const distractors = pool.slice(1, 4);
    const options = shuffle([target, ...distractors]);
    setQuestion({ target, options });
    setAnswered(null);
  }, [nonDisputed]);

  useEffect(() => { if (nonDisputed.length) nextRound(); }, [nonDisputed, nextRound]);

  const onPick = (code) => {
    if (answered) return;
    const correct = code === question.target.code;
    setAnswered({ picked: code, correct });
    if (correct) setScore((s) => s + 1);
    trackEvent("quiz_answer", { country_code: question.target.code, correct, lang });
    const total = round + 1;
    if (total % 10 === 0) {
      const final = score + (correct ? 1 : 0);
      trackEvent("quiz_completed", { score: final, total, lang });
      earnPoints("quiz_played", lang);
      if (final >= 7) earnPoints("quiz_won", lang);
    }
  };

  const onNext = () => { setRound((r) => r + 1); nextRound(); };
  const onReset = () => {
    if (round > 0) trackEvent("quiz_reset", { score, total: round + (answered ? 1 : 0), lang });
    setScore(0); setRound(0); nextRound();
  };

  if (!question) {
    return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  }

  const tab = (on, Icon, label, testid) => (
    <button onClick={() => setDaily(on)} data-testid={testid} aria-pressed={daily === on}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors ${daily === on ? "bg-ink text-sand" : "border border-stoneLine hover:bg-sandDeep/50 text-ink"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
      <header className="text-center mb-8">
        <h1 className="text-4xl sm:text-5xl font-display font-bold text-ink tracking-tight">{t("quiz")}</h1>
        <p className="mt-2 text-muted-foreground">{daily ? t("daily_sub") : t("quiz_intro")}</p>
        <div className="mt-5 flex justify-center gap-2" data-testid="quiz-mode">
          {tab(true, Flame, t("daily_challenge"), "quiz-mode-daily")}
          {tab(false, Dumbbell, t("quiz_practice"), "quiz-mode-practice")}
        </div>
      </header>

      {daily ? <DailyChallenge countries={countries} /> : (<>

      {/* Score bar */}
      <div className="flex items-center justify-between mb-8 px-4">
        <div className="text-sm">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground me-2">{t("quiz_score")}</span>
          <span className="stat-number text-2xl text-ochre" data-testid="quiz-score">{score}</span>
          <span className="text-muted-foreground mx-1.5">/</span>
          <span className="text-muted-foreground stat-number text-lg">{round + (answered ? 1 : 0)}</span>
        </div>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-stoneLine hover:bg-sandDeep/50 transition-colors"
          data-testid="quiz-reset"
        >
          <RotateCcw className="w-4 h-4" /> {t("reset")}
        </button>
      </div>

      {/* Flag card */}
      <div className="rounded-3xl border border-stoneLine bg-card p-6 sm:p-10 flex flex-col items-center gap-8">
        <motion.img
          key={question.target.code}
          src={question.target.flag_url}
          alt="Flag"
          {...FLAG_IN}
          className="w-56 h-36 sm:w-72 sm:h-48 object-cover rounded-xl shadow-md border border-stoneLine"
          data-testid="quiz-flag"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
          {question.options.map((c) => {
            const isTarget = c.code === question.target.code;
            const isPicked = answered && answered.picked === c.code;
            let cls = "border-stoneLine hover:bg-sandDeep/40";
            if (answered) {
              if (isTarget) cls = "border-oasis bg-oasis/10 text-oasis";
              else if (isPicked) cls = "border-ochre bg-ochre/10 text-ochre";
              else cls = "border-stoneLine opacity-60";
            }
            return (
              <button
                key={c.code}
                onClick={() => onPick(c.code)}
                disabled={!!answered}
                className={`px-4 py-3 rounded-xl border text-start transition-colors flex items-center justify-between ${cls}`}
                data-testid={`quiz-option-${c.code}`}
              >
                <span className="font-medium">{c.short_names[lang]}</span>
                {answered && isTarget && <Check className="w-4 h-4" />}
                {answered && isPicked && !isTarget && <X className="w-4 h-4" />}
              </button>
            );
          })}
        </div>

        <AnimatePresence>
          {answered && (
            <motion.div {...FEEDBACK_IN} className="w-full flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm" data-testid="quiz-feedback">
                {answered.correct ? (
                  <span className="text-oasis font-semibold">{t("quiz_correct")}</span>
                ) : (
                  <span className="text-ochre">{t("quiz_wrong")} <b>{question.target.short_names[lang]}</b></span>
                )}
              </div>
              <button
                onClick={onNext}
                className="px-5 py-2.5 rounded-full bg-ink text-sand hover:bg-ochre transition-colors text-sm font-medium"
                data-testid="quiz-next"
              >
                {t("quiz_next")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </>)}
    </div>
  );
}
